import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Reorder } from 'framer-motion';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { api, buildTenantUrl } from '../config/api';
import {
  BookOpenIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  CheckCircleIcon,
  UsersIcon,
  XMarkIcon,
  Square3Stack3DIcon,
  BuildingOffice2Icon,
  ArrowRightIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  EyeSlashIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import { Document, Page, pdfjs } from 'react-pdf';

// Same pdf.js worker as Dhyana / AdminBookApprovals (served from public/).
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// ── Types (mirror backend schemas/course.py) ──────────────────────────────
interface CourseItem {
  id: string;
  item_type: 'reading' | 'assignment';
  position: number;
  placement: string;
  book_id?: string | null;
  page_start?: number | null;
  page_end?: number | null;
  assignment_id?: string | null;
  book_title?: string | null;
  book_cover_url?: string | null;
  assignment_title?: string | null;
}
interface Course {
  id: string;
  institution_id: string;
  created_by: string;
  title: string;
  description?: string | null;
  status: string;
  item_count?: number | null;
}
interface CourseDetail extends Course {
  items: CourseItem[];
}
interface BookOption {
  id: string;
  title: string;
  author: string;
  cover_url: string | null;
  processed: boolean; // has page metadata → TL;DRs / chat hints work in the reader
}
interface BookChapter {
  number: string;
  name: string;
  page_start: number;
  page_end: number | null;
}
interface BookStructure {
  id: string;
  title: string;
  author?: string | null;
  total_pages?: number | null;
  cover_url?: string | null;
  read_url?: string | null;
  chapters: BookChapter[];
}
interface GroupOption {
  id: string;
  name: string;
  student_count?: number | null;
}

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

// ── Small shared primitives ───────────────────────────────────────────────
const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-600 dark:text-cyan-400">{children}</div>
);

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const published = status === 'published';
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold',
        published ? 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300' : 'bg-gray-100 text-gray-500 dark:bg-slate-800 dark:text-slate-400',
      )}
    >
      <span className={cx('h-1.5 w-1.5 rounded-full', published ? 'bg-cyan-500' : 'bg-gray-400')} />
      {published ? 'Published' : 'Draft'}
    </span>
  );
};

const BookCover: React.FC<{ url?: string | null; title?: string | null; className?: string }> = ({ url, title, className }) => {
  const [err, setErr] = useState(false);
  if (url && !err) {
    return <img src={url} alt={title || ''} onError={() => setErr(true)} className={cx('object-cover', className)} />;
  }
  return (
    <div className={cx('flex items-center justify-center bg-gradient-to-br from-cyan-700 via-cyan-500 to-sky-400 text-white/90', className)}>
      <BookOpenIcon className="h-1/3 w-1/3" />
    </div>
  );
};

const Spinner: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cx('animate-spin rounded-full border-b-2 border-cyan-500', className || 'h-8 w-8')} />
);

// ── Hook: fetch a book's structure (cover, chapters, page count, read url) ─
function useBookStructure(bookId?: string | null) {
  const [data, setData] = useState<BookStructure | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!bookId) {
      setData(null);
      return;
    }
    let alive = true;
    setLoading(true);
    api
      .get(`/api/courses/book-structure/${bookId}`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [bookId]);
  return { data, loading };
}

// ── PDF range viewer — renders pages with react-pdf (canvas, no download),
//    like Dhyana. Opens at `targetPage`, navigable within [minPage, maxPage]. ──
const PdfRangeViewer: React.FC<{ url?: string | null; targetPage: number; minPage?: number; maxPage?: number; isDarkMode: boolean; fill?: boolean }> = ({ url, targetPage, minPage = 1, maxPage, isDarkMode, fill }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(targetPage);
  const [width, setWidth] = useState(360);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setPage(targetPage);
    setFailed(false);
  }, [targetPage, url]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Track the container's real width (handles the pane expanding after mount,
    // sidebar toggles, window resize) so the page always renders at full width.
    const measure = () => setWidth(Math.max(200, el.clientWidth - 24));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [url, fill]);

  if (!url) {
    return (
      <div className={cx('flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed', fill ? 'h-full' : 'h-[380px]', isDarkMode ? 'border-slate-700 text-slate-500' : 'border-gray-200 text-gray-400')}>
        <BookOpenIcon className="h-8 w-8" />
        <span className="text-xs">Preview unavailable</span>
      </div>
    );
  }

  const upper = maxPage || numPages || targetPage;
  const safePage = Math.min(Math.max(page, 1), numPages || page);

  return (
    <div className={cx('flex flex-col gap-2', fill && 'h-full min-h-0')}>
      <div ref={ref} className={cx('relative overflow-auto rounded-xl bg-gray-100 ring-1 ring-black/5', fill ? 'min-h-0 flex-1' : 'h-[380px]')}>
        {failed ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-500">
            <BookOpenIcon className="h-8 w-8" />
            <span className="text-xs">Couldn't load preview</span>
          </div>
        ) : (
          <Document
            file={url}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            onLoadError={() => setFailed(true)}
            loading={<div className="flex h-full min-h-[300px] items-center justify-center"><Spinner /></div>}
            error={<div className="flex h-full min-h-[300px] items-center justify-center text-xs text-gray-500">Couldn't load preview</div>}
            className="flex justify-center py-2"
          >
            <Page pageNumber={safePage} width={width} renderTextLayer={false} renderAnnotationLayer={false} loading={<div className="flex h-[300px] items-center justify-center"><Spinner /></div>} />
          </Document>
        )}
      </div>
      <div className="flex flex-none items-center justify-center gap-3 text-xs">
        <button onClick={() => setPage((p) => Math.max(minPage, p - 1))} disabled={safePage <= minPage} className={cx('rounded-lg px-2.5 py-1 font-medium transition disabled:opacity-30', isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-600 hover:bg-gray-100')}>‹ Prev</button>
        <span className={cx('font-mono tabular-nums', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>Page {safePage}{upper ? ` / ${upper}` : ''}</span>
        <button onClick={() => setPage((p) => Math.min(upper, p + 1))} disabled={safePage >= upper} className={cx('rounded-lg px-2.5 py-1 font-medium transition disabled:opacity-30', isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-600 hover:bg-gray-100')}>Next ›</button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Course list (landing) — /teacher/courses
// ════════════════════════════════════════════════════════════════════════
const CourseList: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get('/api/courses').then((r) => setCourses(r.data)).catch(() => setCourses([])).finally(() => setLoading(false));
  }, []);

  const createCourse = async () => {
    setCreating(true);
    try {
      const r = await api.post('/api/courses', { title: 'Untitled course' });
      navigate(`/teacher/courses/${r.data.id}`);
    } catch (e: any) {
      showToast('error', 'Could not create course', e.response?.data?.detail);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-8 py-10">
      <Eyebrow>Course Studio</Eyebrow>
      <div className="mt-2 flex items-center justify-between gap-4">
        <h1 className={cx('text-3xl font-bold tracking-tight', isDarkMode ? 'text-white' : 'text-gray-900')}>Your courses</h1>
        <button
          onClick={createCourse}
          disabled={creating}
          className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-cyan-600 disabled:opacity-60"
        >
          <PlusIcon className="h-4 w-4" /> New course
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : courses.length === 0 ? (
          <div className={cx('rounded-2xl border border-dashed p-12 text-center', isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'border-gray-200 bg-white')}>
            <Square3Stack3DIcon className="mx-auto h-10 w-10 text-cyan-500" />
            <h3 className={cx('mt-4 text-lg font-bold', isDarkMode ? 'text-white' : 'text-gray-900')}>Build your first course</h3>
            <p className={cx('mx-auto mt-1.5 max-w-sm text-sm', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>
              A course is an ordered sequence of readings and assignments you publish to a class.
            </p>
            <button onClick={createCourse} disabled={creating} className="mt-5 inline-flex items-center gap-2 rounded-xl border border-cyan-500/40 bg-cyan-500/10 px-5 py-3 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-500/20 disabled:opacity-60 dark:text-cyan-300">
              <PlusIcon className="h-4 w-4" /> New course
            </button>
          </div>
        ) : (
          courses.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/teacher/courses/${c.id}`)}
              className={cx(
                'flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition hover:-translate-y-0.5',
                isDarkMode ? 'border-slate-700 bg-slate-900/50 hover:border-cyan-500/40' : 'border-gray-200 bg-white hover:border-cyan-500/40 hover:shadow-md',
              )}
            >
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-300"><Square3Stack3DIcon className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <div className={cx('truncate font-semibold', isDarkMode ? 'text-white' : 'text-gray-900')}>{c.title}</div>
                <div className={cx('text-sm', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>{c.item_count ?? 0} item{(c.item_count ?? 0) === 1 ? '' : 's'}</div>
              </div>
              <StatusPill status={c.status} />
            </button>
          ))
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  RIGHT PANE — Add a reading (browse books → book opens with TOC + preview)
// ════════════════════════════════════════════════════════════════════════
const AddReadingPane: React.FC<{ courseId: string; institutionId: string; isDarkMode: boolean; onAdded: () => void }> = ({ courseId, institutionId, isDarkMode, onAdded }) => {
  const [books, setBooks] = useState<BookOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [pickedId, setPickedId] = useState<string | null>(null);

  useEffect(() => {
    api
      .get(`/api/institutions/${institutionId}/books`)
      .then((r) => setBooks(r.data.map((b: any) => ({ id: b.id, title: b.title, author: b.author, cover_url: b.cover_url, processed: !!b.processed }))))
      .catch(() => setBooks([]))
      .finally(() => setLoading(false));
  }, [institutionId]);

  const picked = books.find((b) => b.id === pickedId) || null;
  if (picked) {
    return <BookInspectorPane book={picked} courseId={courseId} isDarkMode={isDarkMode} onBack={() => setPickedId(null)} onAdded={onAdded} />;
  }

  const filtered = books.filter((b) => `${b.title} ${b.author}`.toLowerCase().includes(query.toLowerCase()));
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-slate-700/60">
        <BookOpenIcon className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
        <span className={cx('text-sm font-semibold', isDarkMode ? 'text-white' : 'text-gray-900')}>Choose a book</span>
      </div>
      <div className="px-4 pt-3">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search books…"
            className={cx('w-full rounded-lg border py-2 pl-9 pr-3 text-sm', isDarkMode ? 'border-slate-600 bg-slate-800 text-white placeholder-slate-500' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400')}
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        {loading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className={cx('py-10 text-center text-sm', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>No books found.</div>
        ) : (
          <div className="space-y-2">
            {filtered.map((b) => (
              <button
                key={b.id}
                onClick={() => setPickedId(b.id)}
                className={cx('group flex w-full items-center gap-3 rounded-xl border p-2.5 text-left transition', isDarkMode ? 'border-slate-700/70 hover:border-cyan-500/50 hover:bg-slate-800/50' : 'border-gray-200 hover:border-cyan-500/50 hover:bg-cyan-50/40')}
              >
                <BookCover url={b.cover_url} title={b.title} className={cx('h-14 w-10 flex-none rounded shadow-sm', !b.processed && 'opacity-60')} />
                <span className="min-w-0 flex-1">
                  <span className={cx('block truncate text-sm font-semibold', isDarkMode ? 'text-white' : 'text-gray-900')}>{b.title}</span>
                  <span className={cx('block truncate text-xs', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>{b.author}</span>
                  {!b.processed && (
                    <span
                      className="mt-1 inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400"
                      title="This book has no page summaries yet — readings from it show empty TL;DRs and chat hints until it's processed."
                    >
                      No summaries yet
                    </span>
                  )}
                </span>
                <ArrowRightIcon className="h-4 w-4 flex-none text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-cyan-500" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const BookInspectorPane: React.FC<{ book: BookOption; courseId: string; isDarkMode: boolean; onBack: () => void; onAdded: () => void }> = ({ book, courseId, isDarkMode, onBack, onAdded }) => {
  const { showToast } = useToast();
  const { data: s, loading } = useBookStructure(book.id);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(20);
  const [previewPage, setPreviewPage] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (s?.total_pages) setTo(Math.min(20, s.total_pages));
  }, [s]);

  const toggleChapter = (c: BookChapter) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(c.number) ? n.delete(c.number) : n.add(c.number);
      return n;
    });
    setPreviewPage(c.page_start);
  };

  const selectedCount = selected.size;
  const add = async () => {
    const chaps = (s?.chapters || []).filter((c) => selected.has(c.number));
    const ranges = chaps.length > 0 ? chaps.map((c) => ({ page_start: c.page_start, page_end: c.page_end ?? c.page_start })) : [{ page_start: from, page_end: to }];
    setSaving(true);
    try {
      for (const r of ranges) {
        await api.post(`/api/courses/${courseId}/items`, { item_type: 'reading', book_id: book.id, page_start: r.page_start, page_end: r.page_end });
      }
      showToast('success', ranges.length > 1 ? `Added ${ranges.length} readings` : 'Reading added');
      onAdded();
    } catch (e: any) {
      showToast('error', 'Could not add reading', e.response?.data?.detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* header */}
      <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-slate-700/60">
        <button onClick={onBack} title="Back to books" className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-slate-800 dark:hover:text-white">
          <ArrowLeftIcon className="h-4 w-4" />
        </button>
        <BookCover url={s?.cover_url || book.cover_url} title={book.title} className="h-10 w-7 flex-none rounded shadow-sm" />
        <div className="min-w-0">
          <div className={cx('truncate text-sm font-semibold', isDarkMode ? 'text-white' : 'text-gray-900')}>{book.title}</div>
          <div className={cx('truncate text-xs', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>{book.author}{s?.total_pages ? ` · ${s.total_pages} pages` : ''}</div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center"><Spinner /></div>
      ) : (
        <>
          {/* the book "opens" */}
          <div className="px-4 pt-4">
            <PdfRangeViewer url={s?.read_url} targetPage={previewPage} minPage={1} maxPage={s?.total_pages || undefined} isDarkMode={isDarkMode} />
          </div>

          {/* chapter table of contents */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {s?.chapters && s.chapters.length > 0 ? (
              <>
                <div className={cx('mb-2 text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>Tag chapters</div>
                <div className="space-y-1.5">
                  {s.chapters.map((c) => {
                    const on = selected.has(c.number);
                    return (
                      <button
                        key={c.number}
                        onClick={() => toggleChapter(c)}
                        className={cx('flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition', on ? 'border-cyan-500/50 bg-cyan-500/10' : isDarkMode ? 'border-slate-700/60 hover:border-cyan-500/40' : 'border-gray-200 hover:border-cyan-500/40')}
                      >
                        <span className={cx('flex h-5 w-5 flex-none items-center justify-center rounded-md border', on ? 'border-cyan-500 bg-cyan-500 text-white' : isDarkMode ? 'border-slate-600' : 'border-gray-300')}>
                          {on && <CheckIcon className="h-3.5 w-3.5" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={cx('block truncate text-sm font-medium', isDarkMode ? 'text-white' : 'text-gray-900')}>{c.name}</span>
                        </span>
                        <span className={cx('flex-none font-mono text-xs tabular-nums', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>pp. {c.page_start}{c.page_end ? `–${c.page_end}` : ''}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className={cx('text-xs', isDarkMode ? 'text-slate-500' : 'text-gray-400')}>This book has no chapter map — set a page range below.</div>
            )}

            {/* manual range (used when no chapters tagged) */}
            <div className={cx('mt-4 rounded-lg border p-3', selectedCount > 0 ? 'opacity-40' : '', isDarkMode ? 'border-slate-700/60' : 'border-gray-200')}>
              <div className={cx('mb-2 text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>Or a custom page range</div>
              <div className="flex items-center gap-2">
                <input type="number" min={1} value={from} disabled={selectedCount > 0} onChange={(e) => setFrom(Math.max(1, +e.target.value))} className={cx('w-20 rounded-lg border px-2 py-1.5 text-sm', isDarkMode ? 'border-slate-600 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-900')} />
                <span className={isDarkMode ? 'text-slate-500' : 'text-gray-400'}>→</span>
                <input type="number" min={from} value={to} disabled={selectedCount > 0} onChange={(e) => setTo(+e.target.value)} className={cx('w-20 rounded-lg border px-2 py-1.5 text-sm', isDarkMode ? 'border-slate-600 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-900')} />
              </div>
            </div>
          </div>

          {/* footer */}
          <div className="border-t border-gray-200 p-3 dark:border-slate-700/60">
            <button onClick={add} disabled={saving} className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-cyan-600 disabled:opacity-60">
              <PlusIcon className="h-4 w-4" />
              {selectedCount > 0 ? `Add ${selectedCount} reading${selectedCount === 1 ? '' : 's'}` : 'Add reading'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  RIGHT PANE — Preview a reading's range
// ════════════════════════════════════════════════════════════════════════
const ReadingPreviewPane: React.FC<{ item: CourseItem; isDarkMode: boolean }> = ({ item, isDarkMode }) => {
  const { data: s, loading } = useBookStructure(item.book_id || undefined);
  const start = item.page_start || 1;
  const covered = (s?.chapters || []).filter((c) => {
    const cEnd = c.page_end ?? c.page_start;
    return (item.page_start ?? 0) <= cEnd && (item.page_end ?? Infinity) >= c.page_start;
  });
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-gray-200 py-3 pl-4 pr-12 dark:border-slate-700/60">
        <BookCover url={s?.cover_url || item.book_cover_url} title={item.book_title} className="h-10 w-7 flex-none rounded shadow-sm" />
        <div className="min-w-0 flex-1">
          <div className={cx('truncate text-sm font-semibold', isDarkMode ? 'text-white' : 'text-gray-900')}>{item.book_title || 'Reading'}</div>
          <div className="inline-flex items-center gap-1 text-xs text-cyan-700 dark:text-cyan-300"><DocumentTextIcon className="h-3 w-3" /> Pages {item.page_start ?? '?'}–{item.page_end ?? '?'}</div>
        </div>
      </div>
      {covered.length > 0 && (
        <div className="flex flex-none flex-wrap items-center gap-1.5 border-b border-gray-200 px-4 py-2 dark:border-slate-700/60">
          <span className={cx('mr-1 text-[11px] font-semibold uppercase tracking-wider', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>In range</span>
          {covered.map((c) => (
            <span key={c.number} className="inline-flex items-center rounded-full bg-cyan-500/10 px-2.5 py-0.5 text-xs font-medium text-cyan-700 dark:text-cyan-300">{c.name}</span>
          ))}
        </div>
      )}
      {loading ? (
        <div className="flex flex-1 items-center justify-center"><Spinner /></div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col p-4">
          <PdfRangeViewer fill url={s?.read_url} targetPage={start} minPage={item.page_start || 1} maxPage={item.page_end || undefined} isDarkMode={isDarkMode} />
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Assign-to-class modal
// ════════════════════════════════════════════════════════════════════════
const AssignModal: React.FC<{ institutionId: string; courseId: string; onClose: () => void }> = ({ institutionId, courseId, onClose }) => {
  const { isDarkMode } = useTheme();
  const { showToast } = useToast();
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/api/institutions/${institutionId}/groups`).then((r) => setGroups(r.data)).catch(() => setGroups([])).finally(() => setLoading(false));
  }, [institutionId]);

  const assign = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post(`/api/courses/${courseId}/assign/group`, { group_id: selected });
      showToast('success', 'Course assigned to class');
      onClose();
    } catch (e: any) {
      showToast('error', 'Could not assign course', e.response?.data?.detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={cx('w-full max-w-md rounded-2xl p-6', isDarkMode ? 'bg-slate-800' : 'bg-white')}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className={cx('text-lg font-semibold', isDarkMode ? 'text-white' : 'text-gray-900')}>Assign to a class</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : groups.length === 0 ? (
          <div className={cx('py-6 text-center text-sm', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>No classes in this institution yet.</div>
        ) : (
          <div className="space-y-2">
            {groups.map((g) => (
              <button key={g.id} onClick={() => setSelected(g.id)} className={cx('flex w-full items-center gap-3 rounded-xl border p-3 text-left transition', selected === g.id ? 'border-cyan-500/50 bg-cyan-500/5' : isDarkMode ? 'border-slate-700 hover:border-cyan-500/40' : 'border-gray-200 hover:border-cyan-500/40')}>
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-300"><UsersIcon className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className={cx('block truncate font-medium', isDarkMode ? 'text-white' : 'text-gray-900')}>{g.name}</span>
                  {typeof g.student_count === 'number' && <span className={cx('block text-sm', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>{g.student_count} students</span>}
                </span>
                {selected === g.id && <CheckCircleIcon className="h-5 w-5 text-cyan-500" />}
              </button>
            ))}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className={cx('rounded-xl px-4 py-2.5 text-sm font-semibold', isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-gray-600 hover:bg-gray-100')}>Cancel</button>
          <button onClick={assign} disabled={!selected || saving} className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-cyan-600 disabled:opacity-50"><UsersIcon className="h-4 w-4" /> Assign</button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Course editor — two-pane workspace
// ════════════════════════════════════════════════════════════════════════
type RightMode = 'idle' | 'add' | 'preview';

const CourseEditor: React.FC<{ courseId: string }> = ({ courseId }) => {
  const { isDarkMode } = useTheme();
  const { showToast } = useToast();
  const { tenant } = useTenant();
  const institutionId = tenant?.institution_id;

  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [rightMode, setRightMode] = useState<RightMode>('idle');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const itemsRef = useRef<CourseItem[]>([]);
  itemsRef.current = course?.items || [];

  // Resizable, on-demand side panel (only opens for preview / attach).
  const containerRef = useRef<HTMLDivElement>(null);
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem('cs_panel_w'));
    return saved >= 380 ? saved : 580;
  });
  const panelWidthRef = useRef(panelWidth);
  panelWidthRef.current = panelWidth;
  const resizingRef = useRef(false);
  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    resizingRef.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!resizingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setPanelWidth(Math.min(rect.width - 380, Math.max(380, rect.right - e.clientX)));
    };
    const onUp = () => {
      if (!resizingRef.current) return;
      resizingRef.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      localStorage.setItem('cs_panel_w', String(Math.round(panelWidthRef.current)));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/courses/${courseId}`);
      setCourse(r.data);
    } catch {
      setCourse(null);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveTitle = async () => {
    if (!course || !titleDraft.trim()) {
      setEditingTitle(false);
      return;
    }
    const r = await api.patch(`/api/courses/${courseId}`, { title: titleDraft.trim() });
    setCourse((c) => (c ? { ...c, title: r.data.title } : c));
    setEditingTitle(false);
  };

  const removeItem = async (itemId: string) => {
    await api.delete(`/api/courses/${courseId}/items/${itemId}`);
    if (selectedItemId === itemId) {
      setSelectedItemId(null);
      setRightMode('idle');
    }
    await load();
  };

  const persistOrder = useCallback(async () => {
    try {
      await api.patch(`/api/courses/${courseId}/reorder`, { item_ids: itemsRef.current.map((i) => i.id) });
    } catch {
      showToast('error', 'Could not save the new order');
      load();
    }
  }, [courseId, load, showToast]);

  const publish = async () => {
    try {
      const r = await api.post(`/api/courses/${courseId}/publish`);
      setCourse((c) => (c ? { ...c, status: r.data.status } : c));
      showToast('success', 'Course published');
    } catch (e: any) {
      showToast('error', 'Could not publish', e.response?.data?.detail);
    }
  };

  const unpublish = async () => {
    try {
      const r = await api.patch(`/api/courses/${courseId}`, { status: 'draft' });
      setCourse((c) => (c ? { ...c, status: r.data.status } : c));
      showToast('success', 'Course unpublished');
    } catch (e: any) {
      showToast('error', 'Could not unpublish', e.response?.data?.detail);
    }
  };

  const openAdd = () => {
    setSelectedItemId(null);
    setRightMode('add');
  };
  const selectItem = (item: CourseItem) => {
    setSelectedItemId(item.id);
    setRightMode('preview');
  };
  const closePanel = () => {
    setRightMode('idle');
    setSelectedItemId(null);
  };

  if (loading) return <div className="flex justify-center py-24"><Spinner /></div>;
  if (!course) return <div className={cx('mx-auto max-w-md px-8 py-24 text-center', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>Course not found, or you don't have access to it.</div>;

  const selectedItem = course.items.find((i) => i.id === selectedItemId) || null;
  const readings = course.items.filter((i) => i.item_type === 'reading').length;
  const assignments = course.items.filter((i) => i.item_type === 'assignment').length;
  const panelOpen = rightMode === 'add' || rightMode === 'preview';

  return (
    <div className="px-6 py-6 lg:h-[calc(100vh-4rem)] lg:overflow-hidden">
      <div ref={containerRef} className="lg:flex lg:h-full">
        {/* ── timeline — full width until a panel opens ──────────────── */}
        <div className="min-w-0 lg:flex-1 lg:overflow-y-auto">
          <div className="mx-auto max-w-3xl pb-8">
          <Eyebrow>Course Studio</Eyebrow>
          <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2.5">
                {editingTitle ? (
                  <input
                    autoFocus
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onBlur={saveTitle}
                    onKeyDown={(e) => e.key === 'Enter' && saveTitle()}
                    className={cx('min-w-0 flex-1 rounded-lg border px-2 py-1 text-2xl font-bold tracking-tight', isDarkMode ? 'border-slate-600 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-900')}
                  />
                ) : (
                  <h1 className={cx('truncate text-2xl font-bold tracking-tight', isDarkMode ? 'text-white' : 'text-gray-900')}>{course.title}</h1>
                )}
                {!editingTitle && (
                  <button onClick={() => { setTitleDraft(course.title); setEditingTitle(true); }} title="Rename" className="text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400"><PencilIcon className="h-4 w-4" /></button>
                )}
                <StatusPill status={course.status} />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setAssignOpen(true)} className={cx('inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm font-semibold transition hover:-translate-y-0.5', isDarkMode ? 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50')}>
                <UsersIcon className="h-4 w-4" /> Assign
              </button>
              {course.status === 'published' ? (
                <button onClick={unpublish} className={cx('inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm font-semibold transition hover:-translate-y-0.5', isDarkMode ? 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50')}><EyeSlashIcon className="h-4 w-4" /> Unpublish</button>
              ) : (
                <button onClick={publish} className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-3.5 py-2 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-cyan-600"><CheckIcon className="h-4 w-4" /> Publish</button>
              )}
            </div>
          </div>

          {/* meta */}
          {course.items.length > 0 && (
            <div className={cx('mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border px-5 py-3 text-sm', isDarkMode ? 'border-slate-700 bg-slate-900/50 text-slate-300' : 'border-gray-200 bg-white text-gray-600')}>
              <span className="inline-flex items-center gap-2"><Square3Stack3DIcon className="h-4 w-4 text-cyan-600 dark:text-cyan-400" /><b className={isDarkMode ? 'text-white' : 'text-gray-900'}>{course.items.length}</b> items</span>
              <span className="h-4 w-px bg-gray-200 dark:bg-slate-700" />
              <span>{readings} reading{readings === 1 ? '' : 's'}</span>
              <span className="h-4 w-px bg-gray-200 dark:bg-slate-700" />
              <span>{assignments} assignment{assignments === 1 ? '' : 's'}</span>
            </div>
          )}

          {/* timeline */}
          <div className="mt-5 pb-6">
            {course.items.length === 0 ? (
              <div className={cx('flex flex-col items-center rounded-2xl border border-dashed px-6 py-12 text-center', isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'border-gray-200 bg-white')}>
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-300"><BookOpenIcon className="h-6 w-6" /></span>
                <h3 className={cx('mt-4 text-base font-bold', isDarkMode ? 'text-white' : 'text-gray-900')}>Start with a reading</h3>
                <p className={cx('mt-1 max-w-xs text-sm', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>Add a reading, then tag chapters or pages from the book.</p>
              </div>
            ) : (
              <Reorder.Group as="div" axis="y" values={course.items} onReorder={(items) => setCourse((c) => (c ? { ...c, items } : c))} className="space-y-2.5">
                {course.items.map((item, idx) => (
                  <ReorderRow
                    key={item.id}
                    item={item}
                    index={idx}
                    selected={item.id === selectedItemId}
                    isDarkMode={isDarkMode}
                    onClick={() => selectItem(item)}
                    onRemove={() => removeItem(item.id)}
                    onDragEnd={persistOrder}
                  />
                ))}
              </Reorder.Group>
            )}

            {/* add affordance */}
            <button
              onClick={openAdd}
              className={cx(
                'mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed py-3 text-sm font-semibold transition',
                rightMode === 'add' ? 'border-cyan-500 text-cyan-600 dark:text-cyan-300' : isDarkMode ? 'border-slate-700 text-slate-400 hover:border-cyan-500 hover:text-cyan-300' : 'border-gray-300 text-gray-500 hover:border-cyan-500 hover:text-cyan-600',
              )}
            >
              <PlusIcon className="h-4 w-4" /> Add reading
            </button>
          </div>
          </div>
        </div>

        {/* ── on-demand resizable panel (preview / attach) ───────────── */}
        {panelOpen && (
          <>
            <div onPointerDown={startResize} title="Drag to resize" className="group hidden w-2.5 flex-none cursor-col-resize items-stretch lg:flex">
              <div className="mx-auto w-px bg-gray-200 transition-colors group-hover:bg-cyan-500 dark:bg-slate-700" />
            </div>
            <div style={{ ['--pw' as any]: `${panelWidth}px` }} className={cx('relative mt-4 flex h-[70vh] w-full flex-none flex-col overflow-hidden rounded-2xl border lg:mt-0 lg:h-full lg:w-[var(--pw)]', isDarkMode ? 'border-slate-700/70 bg-slate-900/40' : 'border-gray-200 bg-white shadow-sm')}>
              <button onClick={closePanel} title="Close" className="absolute right-2 top-2.5 z-20 flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-slate-800 dark:hover:text-white"><XMarkIcon className="h-4 w-4" /></button>
              {rightMode === 'add' && institutionId ? (
                <AddReadingPane courseId={courseId} institutionId={institutionId} isDarkMode={isDarkMode} onAdded={load} />
              ) : rightMode === 'preview' && selectedItem ? (
                <ReadingPreviewPane item={selectedItem} isDarkMode={isDarkMode} />
              ) : null}
            </div>
          </>
        )}
      </div>

      {assignOpen && institutionId && <AssignModal institutionId={institutionId} courseId={courseId} onClose={() => setAssignOpen(false)} />}
    </div>
  );
};

const ReorderRow: React.FC<{ item: CourseItem; index: number; selected: boolean; isDarkMode: boolean; onClick: () => void; onRemove: () => void; onDragEnd: () => void }> = ({ item, index, selected, isDarkMode, onClick, onRemove, onDragEnd }) => {
  const reading = item.item_type === 'reading';
  return (
    <Reorder.Item as="div" value={item} onDragEnd={onDragEnd} className="list-none">
      <div className="flex items-stretch gap-3">
        <div className="flex flex-none flex-col items-center pt-3">
          <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-cyan-500 bg-white text-[11px] font-bold text-cyan-700 dark:bg-slate-900 dark:text-cyan-300">{index + 1}</span>
        </div>
        <div
          onClick={onClick}
          className={cx(
            'flex flex-1 cursor-pointer items-center gap-3 rounded-2xl border p-2.5 transition',
            selected ? 'border-cyan-500 ring-1 ring-cyan-500/40' : isDarkMode ? 'border-slate-700 hover:border-cyan-500/40' : 'border-gray-200 hover:border-cyan-500/40 hover:shadow-sm',
            isDarkMode ? 'bg-slate-900/60' : 'bg-white',
          )}
        >
          <span className="cursor-grab text-gray-300 active:cursor-grabbing dark:text-slate-600" title="Drag to reorder"><ChevronUpDownIcon className="h-5 w-5" /></span>
          {reading ? (
            <BookCover url={item.book_cover_url} title={item.book_title} className="h-14 w-10 flex-none rounded-md shadow" />
          ) : (
            <span className="flex h-14 w-10 flex-none items-center justify-center rounded-md bg-cyan-500/10 text-cyan-600 dark:text-cyan-300"><DocumentTextIcon className="h-5 w-5" /></span>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500">{reading ? 'Reading' : 'Assignment'}</div>
            <p className={cx('truncate text-sm font-semibold', isDarkMode ? 'text-white' : 'text-gray-900')}>{reading ? item.book_title || 'Reading' : item.assignment_title || 'Assignment'}</p>
            {reading && (item.page_start || item.page_end) && (
              <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400"><DocumentTextIcon className="h-3 w-3" /> Pages {item.page_start ?? '?'}–{item.page_end ?? '?'}</div>
            )}
          </div>
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Remove" className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-gray-400 transition hover:bg-red-500/10 hover:text-red-500"><TrashIcon className="h-4 w-4" /></button>
        </div>
      </div>
    </Reorder.Item>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  No-institution notice
// ════════════════════════════════════════════════════════════════════════
const NoInstitutionNotice: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const TEACH = ['institution_admin', 'institution_sub_admin', 'teacher'];
  const seen = new Set<string>();
  const targets = ((user?.institution_memberships || []) as any[])
    .filter((m) => TEACH.includes(m.role) && m.institution_slug && !seen.has(m.institution_slug) && seen.add(m.institution_slug))
    .map((m) => ({ slug: m.institution_slug as string, name: (m.institution_name as string) || m.institution_slug }));

  return (
    <div className="mx-auto max-w-xl px-8 py-20 text-center">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-300"><BuildingOffice2Icon className="h-7 w-7" /></span>
      <h2 className={cx('mt-5 text-2xl font-bold tracking-tight', isDarkMode ? 'text-white' : 'text-gray-900')}>Open Course Studio on your institution</h2>
      <p className={cx('mx-auto mt-2 max-w-md text-sm leading-relaxed', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>
        Courses are scoped to an institution, so Course Studio runs on your institution's space — not the main site.
      </p>
      {targets.length > 0 ? (
        <div className="mt-6 flex flex-col items-center gap-2">
          {targets.map((t) => {
            const url = buildTenantUrl(t.slug, '/teacher/courses');
            return (
              <a key={t.slug} href={url || '#'} className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-cyan-600">Go to {t.name} <ArrowRightIcon className="h-4 w-4" /></a>
            );
          })}
        </div>
      ) : (
        <p className={cx('mt-5 text-xs', isDarkMode ? 'text-slate-500' : 'text-gray-400')}>Switch to your institution's subdomain and open Course Studio there.</p>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Route entry
// ════════════════════════════════════════════════════════════════════════
const CourseStudio: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { tenant } = useTenant();
  return <Layout>{!tenant?.institution_id ? <NoInstitutionNotice /> : courseId ? <CourseEditor courseId={courseId} /> : <CourseList />}</Layout>;
};

export default CourseStudio;
